import type { User } from 'modules/roles-manager/domain/interfaces/user';
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

type PagesCache = Record<number, User[]>;

type UsersPagesCacheContextValue = {
  pagesCache: PagesCache;
  setPageData: (page: number, data: User[]) => void;
  resetPagesCache: () => void;
  pageCount: number;
  setPageCount: (value: number) => void;
};

const UsersPagesCacheContext = createContext<UsersPagesCacheContextValue | undefined>(undefined);

export const UsersPagesCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<PagesCache>({});
  const [pageCount, setPageCount] = useState(1);

  const setPageData = useCallback((page: number, data: User[]) => {
    cacheRef.current[page] = data;
  }, []);

  const resetPagesCache = useCallback(() => {
    cacheRef.current = {};
    setPageCount(1);
  }, []);

  return (
    <UsersPagesCacheContext.Provider
      value={{
        pagesCache: cacheRef.current,
        setPageData,
        resetPagesCache,
        pageCount,
        setPageCount,
      }}
    >
      {children}
    </UsersPagesCacheContext.Provider>
  );
};

export function useUsersPagesCache() {
  const ctx = useContext(UsersPagesCacheContext);
  if (!ctx) {
    throw new Error('useUsersPagesCache must be used within UsersPagesCacheProvider');
  }
  return ctx;
}
